import { SIMULATION_INPUT_VIEW_MODE } from "@/lib/viewModes"

import type {
  EditableTestCase,
  EditableTestCaseRecordLike,
  TestSetMetadata,
} from "../model"

interface TestSetEditorChangesInput {
  activeAgentId: string
  initialActiveAgentId: string
  initialMetadata: TestSetMetadata
  initialSelectedAgentIds: string[]
  initialTestCases: EditableTestCase[]
  localMediaAttachmentCount: number
  metadata: TestSetMetadata
  selectedAgentIds: string[]
  testCases: EditableTestCase[]
}

export function normalizeEditableTestCases(
  records: EditableTestCaseRecordLike[],
): EditableTestCase[] {
  return records.map((record) => ({
    id: record.id,
    input_str: record.input_str || "",
    description: record.description || "",
    expected_behaviors: record.expected_behaviors || [],
    expected_output_json: record.expected_output_json || null,
    s3_key: record.s3_key || null,
    s3_bucket: record.s3_bucket || null,
    input_type: record.input_type || "SCENARIO",
    script_turns: record.script_turns || null,
    simulation_metadata_input: record.simulation_metadata_input || null,
    connected_knowledge_base_entries:
      record.connected_knowledge_base_entries || [],
    media_attachments: record.media_attachments || [],
  }))
}

export function hasKnowledgeBaseEntries(
  testCases: EditableTestCase[],
): boolean {
  return testCases.some(
    (testCase) => (testCase.connected_knowledge_base_entries || []).length > 0,
  )
}

export function getInitialSimulationInputViewMode(
  testCases: EditableTestCase[],
) {
  const hasMarkdownInput = testCases.some((testCase) => {
    const input = testCase.input_str || ""
    return /\*\*|^#+\s|^-\s|\[.*\]\(.*\)/m.test(input)
  })

  return hasMarkdownInput
    ? SIMULATION_INPUT_VIEW_MODE.MARKDOWN
    : SIMULATION_INPUT_VIEW_MODE.TEXT
}

/** Returns whether the shared simulation-wrapper selection or config changed. */
function hasTestSetSimulationWrapperChanges(
  metadata: TestSetMetadata,
  initialMetadata: TestSetMetadata,
): boolean {
  return (
    metadata.simulationWrapperType !== initialMetadata.simulationWrapperType ||
    JSON.stringify(metadata.simulationWrapperTopLevelConfig) !==
      JSON.stringify(initialMetadata.simulationWrapperTopLevelConfig)
  )
}

export function hasTestSetEditorChanges({
  activeAgentId,
  initialActiveAgentId,
  initialMetadata,
  initialSelectedAgentIds,
  initialTestCases,
  localMediaAttachmentCount,
  metadata,
  selectedAgentIds,
  testCases,
}: TestSetEditorChangesInput): boolean {
  if (localMediaAttachmentCount > 0) {
    return true
  }

  if (metadata.description !== initialMetadata.description) {
    return true
  }

  if (hasTestSetSimulationWrapperChanges(metadata, initialMetadata)) {
    return true
  }

  if (testCases.length !== initialTestCases.length) {
    return true
  }

  for (const current of testCases) {
    const original = initialTestCases.find(
      (testCase) => testCase.id === current.id,
    )

    if (!original) {
      return true
    }

    if (
      current.input_str !== original.input_str ||
      current.description !== original.description ||
      JSON.stringify(current.expected_behaviors) !==
        JSON.stringify(original.expected_behaviors) ||
      JSON.stringify(current.expected_output_json) !==
        JSON.stringify(original.expected_output_json) ||
      current.input_type !== original.input_type ||
      JSON.stringify(current.connected_knowledge_base_entries) !==
        JSON.stringify(original.connected_knowledge_base_entries) ||
      JSON.stringify(current.simulation_metadata_input) !==
        JSON.stringify(original.simulation_metadata_input) ||
      JSON.stringify(current.media_attachments) !==
        JSON.stringify(original.media_attachments) ||
      current.s3_key !== original.s3_key ||
      current.s3_bucket !== original.s3_bucket ||
      JSON.stringify(current.script_turns) !==
        JSON.stringify(original.script_turns)
    ) {
      return true
    }
  }

  const currentIds = new Set(testCases.map((testCase) => testCase.id))
  for (const original of initialTestCases) {
    if (!currentIds.has(original.id)) {
      return true
    }
  }

  const currentAgentIds = new Set([...selectedAgentIds].sort())
  const initialAgentIds = new Set([...initialSelectedAgentIds].sort())
  if (
    currentAgentIds.size !== initialAgentIds.size ||
    ![...currentAgentIds].every((id) => initialAgentIds.has(id))
  ) {
    return true
  }

  return activeAgentId !== initialActiveAgentId
}
